import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'App/styled';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import CheckDelivery from '@nykaa/delivery-desktop';
import { State } from 'shared/store/types';
import {
  selectBundleChildIds,
  selectCurrentSku,
} from 'shared/store/product/selector';
import {
  getSelectedCountryData,
  isIndiaSelected,
} from 'shared/helpers/utils/internationalShipping';
import { PRODUCT_TYPE } from '@nykaa/delivery-shared/constants';
import AddToCartButton from '../Actions/AddToCart';
import NotifyMe from '../Actions/NotifyMe';
import WishlistIcon from '../Actions/Wishlist';
import { isRegisteredViaMobile } from 'shared/store/auth/helper';
import ActionWrapper from 'components/CardAction';
import { ProductRenderProps } from 'types/product';
import { PageSource } from 'constants/dataLayer';
import { DEFAULT_SHIPPING_MESSAGE } from 'constants/productPage';
import { CUSTOM_WISHLIST_CLASS } from 'shared/constants/product';
import { AB_NYKAA_NOW_RAAP } from 'constants/experiments';
import { getEligibleForVariant } from 'helpers/experimentV2';

const defaultErrorMessage = 'Information not found for entered pincode.';
interface DeliveryProps {
  handleShowCAB: () => void;
  showDelivery?: boolean;
  showActionButton?: boolean;
}
const Wrapper = styled.div`
  display: flex;
  margin-top: ${({ theme }) => theme.spacing.spacing80};
  margin-bottom: ${({ theme }) => theme.spacing.spacing120};
  padding-bottom: ${({ theme }) => theme.spacing.spacing200};
  width: 100%;
  min-height: 130px;
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-top: ${({ theme }) => theme.spacing.spacing60};
    margin-bottom: ${({ theme }) => theme.spacing.spacing120};
  }
`;

const NotifyWrapper = styled.div`
  width: 100%;
  min-height: 130px;
  margin-top: ${({ theme }) => theme.spacing.spacing80};
  margin-bottom: ${({ theme }) => theme.spacing.spacing120};
`;
const Left = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40%;
  border-right: 1px solid ${({ theme }) => hexToRgb(theme.colors.state, 0.22)};
  min-width: 237px;
  padding-right: ${({ theme }) => theme.spacing.spacing40};
  height: 138px;
  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.spacing.spacing40};
  }
  & > div {
    align-self: center;
  }
  .${CUSTOM_WISHLIST_CLASS} {
    padding: 13px 24px;
    box-shadow: 0px 2px 3px 0 rgb(0 0 0 / 10%);
    border-radius: 3px;
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;
const Right = styled.div`
  width: 60%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${({ theme }) => theme.spacing.spacing120};
  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.spacing.spacing40};
    margin: 0;
  }
`;

const Delivery = ({
  handleShowCAB,
  showDelivery = true,
  showActionButton = true,
}: DeliveryProps) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const regionId = queryParams.get('region_id') || '';
  const deliveryShow = useSelector(
    (state: State) => state.remoteConfig?.switches?.showDeliveryOnPd
  );
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const internationalMode = useSelector(
    (state: State) => state.remoteConfig?.switches?.internationalMode
  );
  const user = useSelector((state: State) => state.auth?.user);
  const defaultShippingMessage =
    useSelector(
      (state: State) => state.remoteConfig?.configs?.defaultShippingMessage
    ) || DEFAULT_SHIPPING_MESSAGE;
  const pincodeApiDisabled = useSelector(
    (state: State) => state.remoteConfig?.configs?.pincodeApiDisabled
  );
  const defaultMessages = {
    defaultShippingMessage,
    defaultErrorMessage,
  };
  const product = useSelector((state: State) => state.productPage.product);
  const isNykaaNowRaapEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_NYKAA_NOW_RAAP, 'A')
  );
  const { onlyWishlistButton, inStock } = currentSku || {};
  const productTrackingData = {
    product: { ...product, secondaryTagV2: currentSku?.secondaryTagV2 },
  };
  const variants = product?.variants;
  /* eslint-disable react-hooks/exhaustive-deps */
  const childSkus =
    variants
      ?.filter((variant) => variant.inStock)
      ?.map((variant) => variant.sku) || [];

  /* eslint-disable react-hooks/exhaustive-deps */
  const productType = product?.productType;
  const bundleChildSku =
    useSelector((state: State) => selectBundleChildIds(state)) || [];
  const getSkus = useCallback(() => {
    if (productType === PRODUCT_TYPE.CONFIGURE && childSkus?.length > 0) {
      return childSkus;
    } else if (
      productType === PRODUCT_TYPE.BUNDLE &&
      bundleChildSku?.length > 0
    ) {
      return bundleChildSku;
    }
    if (currentSku?.sku) {
      return [currentSku?.sku];
    }
    return [];
  }, [currentSku, childSkus, productType, bundleChildSku]);
  return (
    <>
      {!!(inStock || onlyWishlistButton) && (
        <Wrapper>
          {showActionButton && (
            <Left>
              {!!onlyWishlistButton && (
                <WishlistIcon
                  pageLocation={PageSource.Product}
                  customWishlistClass={CUSTOM_WISHLIST_CLASS}
                  productTrackingData={productTrackingData}
                />
              )}
              {!onlyWishlistButton && (
                <AddToCartButton
                  pageLocation={PageSource.Product}
                  handleShowCAB={handleShowCAB}
                  productTrackingData={productTrackingData}
                />
              )}
            </Left>
          )}
          {showDelivery && (
            <Right>
              {deliveryShow && (
                <CheckDelivery
                  productId={product?.id || ''}
                  showCountryList={internationalMode}
                  defaultMessages={defaultMessages}
                  getSelectedCountryData={getSelectedCountryData}
                  isIndiaSelected={isIndiaSelected}
                  pincodeApiDisabled={pincodeApiDisabled}
                  productType={productType}
                  skus={getSkus()}
                  pageType="pdp"
                  skuId={currentSku?.sku}
                  regionId={isNykaaNowRaapEnabled ? regionId : ''}
                />
              )}
            </Right>
          )}
        </Wrapper>
      )}
      {!(inStock || onlyWishlistButton) && showDelivery && (
        <NotifyWrapper>
          <ActionWrapper>
            {({ handleNotifyMe }: ProductRenderProps) => (
              <NotifyMe
                primaryCategories={product?.primaryCategories}
                offerPrice={product?.offerPrice}
                brandName={product?.brandName}
                productId={product?.id}
                productName={product?.name}
                user={user}
                sendNotifyMe={handleNotifyMe}
                isRegisteredViaMobile={isRegisteredViaMobile}
                onClose={() => {}}
                product={product}
                childId={currentSku?.childId}
              />
            )}
          </ActionWrapper>
        </NotifyWrapper>
      )}
    </>
  );
};

export default Delivery;
